import Phaser from 'phaser';
import { SCENE, GAME_WIDTH, GAME_HEIGHT, STREET_SPAWNS } from '../../config.js';
import { visitState } from '../../state/visitState.js';
import { realtime } from '../../api/realtime.js';
import { SceneTransition } from '../../ui/SceneTransition.js';
import { StairsController } from '../../ui/StairsController.js';
import { mainFloorFor, porchSpawn, stairNodesFor, MAIN_SPAWN_AT_STAIRS, MAIN_BIKE_PARKED_DEFAULTS } from '../mainLayout.js';
import { BACKYARD_SPAWN } from '../backyardLayout.js';
import { OUTDOOR_CHARACTER_SCALE, PERSPECTIVE } from '../../ui/carCatalog.mjs';
import { centreForFeet, fitCharacter } from '../../ui/outdoorScale.mjs';
import { loadVisitView, setupVisitWorld, depthSortVisitAvatars } from './VisitSceneBase.js';
import { currentPhase } from '../../state/dayCycle.js';
import { ensureLoaded, loadCtx, prioritiseFor } from '../../ui/sceneLoading.js';
import { needsFor } from '../../assetManifest.mjs';
import { iconText } from '../../ui/iconText.js';
import { showToast } from '../../ui/Toast.js';
import { homeArrival } from '../homeLayout.mjs';
import { hostParkedBike } from '../../ui/hostParkedBike.js';

// Front yard of the host's house. Mirrors MainScene exactly: walk left → back to town
// (ends the visit); climb the stairs → into the host's house. Entering by climbing
// (not a right-edge walk) so visiting a friend feels identical to coming home yourself.
export class VisitMainScene extends Phaser.Scene {
  constructor() { super(SCENE.VISIT_MAIN); }

  async create(data) {
    const view = await loadVisitView(this, data);
    if (!view) return;

    // This scene is NOT built by makeVisitScene, so it must load the host's art itself:
    // their house level picks the yard background, and tiers() never prefetches another
    // level's variants (a level-2 visitor at a level-1 host would get the checkerboard).
    await ensureLoaded(this, needsFor(this.scene.key, loadCtx({ hostState: view })));
    if (!this.sys.isActive()) return;   // scene was stopped while the art was loading
    prioritiseFor(this.scene.key, { hostState: view });

    const lvl = view?.house?.level ?? 1;
    // Sent to a porch spot (out of the house, "Wróć do siebie"): the host's porch may be drawn elsewhere.
    const porched = data?.spawnY == null && data?.spawnFeetY != null ? porchSpawn(lvl, data.spawnX, data.spawnFeetY) : null;
    // Same real-world scale + perspective as the owner MainScene.
    this.worldScale = OUTDOOR_CHARACTER_SCALE;
    this.perspective = PERSPECTIVE;
    setupVisitWorld(this, view, {
      roomScene: 'MainScene',
      yard: { side: 'front', level: lvl },   // the host's yard under the live sky (scenes/yardLook.js)
      areaKey: 'exterior',           // show the host's front-yard decorations
      spawn: porchSpawn(lvl, MAIN_SPAWN_AT_STAIRS.x, MAIN_SPAWN_AT_STAIRS.feetY),   // fresh arrival — at the foot of the host's stairs
      floorPolyline: mainFloorFor(lvl),
      spawnX: porched?.x ?? data?.spawnX,
      spawnY: data?.spawnY ?? (porched ? centreForFeet(this, porched.feetY) : undefined),
    });
    if (data?.toast) showToast(this, data.toast);   // e.g. the host's house is locked (VisitHomeScene)

    // The host's parked bikes (read-only) — same defaults as the owner MainScene.
    // Hover label ("whose bike is this?") shared by the host's parked bikes.
    const bikeTip = iconText(this, 0, 0, `Rowerek: ${visitState.hostName} {bike}`, {
      fontFamily: 'system-ui', fontSize: '16px', color: '#fff',
      backgroundColor: '#000000aa', padding: { x: 8, y: 4 },
    }).setOrigin(0.5, 1).setDepth(50).setVisible(false);
    // Keyed by design id since bike plan 6, `type` the kind (the defaults are per kind).
    for (const [id, b] of Object.entries(view.bikes ?? {})) {
      if (!b?.owned || (b.parked_scene ?? 'MainScene') !== 'MainScene') continue;
      const type = b.type ?? id;
      // Stored y is the rider's centre at dismount; the defaults are parked centres. Sized like the owner's
      // BikeController outdoors: through the ground line, at the yard's perspective.
      hostParkedBike(this, type, b, { x: MAIN_BIKE_PARKED_DEFAULTS[type]?.x ?? GAME_WIDTH / 2, defaultY: MAIN_BIKE_PARKED_DEFAULTS[type]?.y ?? GAME_HEIGHT - 170,
                                      profile: PERSPECTIVE, tip: bikeTip });
    }

    // Left edge → back to town, ending the visit (matches MainScene's "← Miasto").
    this.transition = new SceneTransition(this);
    this.transition.addExitZone({
      side: 'left', toScene: SCENE.OUTSIDE, label: '{arrow_left} Miasto',
      data: { spawnX: STREET_SPAWNS.HOUSE },
      onTrigger: async () => { visitState.reset(); return true; },
    });

    // Right edge → the host's Podwórko, only if they unlocked it (locked → dead edge, no modal).
    if (view?.backyard?.locked === false) {
      this.transition.addExitZone({
        side: 'right', toScene: SCENE.VISIT_BACKYARD, label: 'Podwórko {arrow_right}',
        data: { spawnX: BACKYARD_SPAWN.x, spawnFeetY: BACKYARD_SPAWN.feetY },
      });
    }

    // Climb the stairs to enter the host's house (same mechanic as your own MainScene).
    this.stairs = new StairsController(this, {
      natalka: this.natalka,
      walker: this.walker,
      nodes: stairNodesFor(lvl),         // the host's porch
      onBroadcast: (p) => realtime.move(p),
      onEnterHouse: () => this.scene.start(SCENE.VISIT_HOME, homeArrival('front')),
    });
  }

  update(_t, dMs) {
    // create() is async — the avatar/walker aren't built for the first frame(s).
    if (!this.natalka) return;
    this._yard?.update(currentPhase(), dMs);   // time of day follows the global cycle, as at home
    this.animals?.update(dMs);
    this.stairs.update();
    if (!this.stairs.isOnStairs) {
      this.walker.update(dMs);
      this.transition.checkExits(this.natalka);
    }
    fitCharacter(this);
    depthSortVisitAvatars(this);       // the visitor behind the host is drawn behind her (was a fixed depth 10)
  }
}
